/**
 * Mirrors the JSON the Go API returns. Keeping these in one file means a
 * change on the server shows up as a type error here rather than as an
 * `undefined` at runtime.
 */

export const LOCALES = ['uk', 'en'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'uk'

export function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (LOCALES as readonly string[]).includes(value)
}

export type Role = 'admin' | 'catalog_manager' | 'editor'

export type Permission =
  | 'categories:manage'
  | 'artworks:manage'
  | 'pages:manage'
  | 'users:manage'
  | 'messages:read'
  | 'audit:view'

export interface MediaVariant {
  label: 'thumb' | 'card' | 'full'
  format: string
  width: number
  height: number
  bytes: number
  url: string
}

export interface Media {
  id: string
  format: string
  width: number
  height: number
  bytes: number
  /** Inline data URI shown blurred while the real photo downloads. */
  placeholder: string
  altUk: string
  altEn: string
  createdAt: string
  variants: MediaVariant[]
  url: string
}

export interface Translation {
  locale: Locale
  title: string
  description: string
}

export interface Category {
  id: string
  slug: string
  position: number
  isPublished: boolean
  iconMediaId?: string
  coverMediaId?: string
  title: string
  description: string
  artworkCount: number
  icon?: Media
  cover?: Media
  /** Present only in admin responses. */
  translations?: Translation[]
}

export interface Artwork {
  id: string
  /** Short, permanent number used in the public URL: /uk/work/42-kit-bandyt. */
  publicId: number
  slug: string
  categoryId: string
  categorySlug: string
  categoryTitle: string
  /** ISO date, or absent when the date is unknown. */
  createdOn?: string
  position: number
  isPublished: boolean
  title: string
  description: string
  cover?: Media
  photos?: Media[]
  translations?: Translation[]
  updatedAt: string
}

export interface PageTranslation {
  locale: Locale
  title: string
  body: string
  metaTitle: string
  metaDescription: string
}

export interface SitePage {
  id: string
  slug: string
  title: string
  body: string
  metaTitle: string
  metaDescription: string
  cover?: Media
  updatedAt: string
  translations?: PageTranslation[]
}

export interface ContactMessage {
  id: string
  name: string
  email: string
  message: string
  locale: Locale
  isRead: boolean
  createdAt: string
}

export interface User {
  id: string
  email: string
  displayName: string
  role: Role
  isActive: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
  permissions?: Permission[]
}

export interface Session {
  user: User
  permissions: Permission[]
}

export interface AuditEntry {
  id: number
  actorName: string
  action: string
  entityType: string
  entityId: string
  details: unknown
  createdAt: string
}

export interface Paginated<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

export interface SitemapData {
  locales: Locale[]
  categories: string[]
  artworks: { publicId: number; slug: string; updatedAt: string }[]
  pages: string[]
}
